'use client'

import { AnimatePresence, m } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useRef, useState } from 'react'

import { CaseCardDTO } from '@/actions/cases'

const categories = [
  { id: 'all', label: 'Все' },
  { id: 'dev', label: 'Разработка' },
  { id: 'mkt', label: 'Маркетинг' },
  { id: 'des', label: 'Дизайн' },
]

interface CasesProps {
  initialCases: CaseCardDTO[]
}

type CaseResult = CaseCardDTO['results'][number]

export default function Cases({ initialCases = [] }: CasesProps) {
  const [activeCategory, setActiveCategory] = useState('all')
  const carouselRef = useRef<HTMLDivElement>(null)
  const router = useRouter()

  const prefetchRoute = (href: string) => {
    try {
      router.prefetch(href)
    } catch {
      // no-op
    }
  }

  const projectsToDisplay = initialCases

  const filteredProjects =
    activeCategory === 'all'
      ? projectsToDisplay
      : projectsToDisplay.filter((p) => p.category === activeCategory)

  return (
    <section className="home-section relative overflow-hidden">
      <div className="home-container relative z-10">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:mb-12 sm:gap-6 lg:flex-row lg:items-end lg:justify-between lg:gap-8">
          <m.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="home-title leading-tight text-white"
          >
            Наши
            <span className="ml-2 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent sm:ml-4">
              кейсы
            </span>
          </m.h2>

          {/* Filter */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="flex w-full flex-wrap justify-center gap-2 lg:w-auto lg:justify-end"
          >
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`rounded-full px-4 py-2.5 text-xs font-medium transition-all duration-300 sm:px-6 sm:py-3 sm:text-sm ${
                  activeCategory === cat.id
                    ? 'bg-white text-black'
                    : 'border border-white/10 bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </m.div>
        </div>

        {/* Projects Grid / Carousel */}
        <div className="relative">
          {/* Removed 'group' from parent div to avoid sharing hover effects */}
          <m.div
            ref={carouselRef}
            layout
            className="scrollbar-hide -mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-8 sm:mx-0 sm:px-0 sm:pb-0 md:grid md:grid-cols-2 md:gap-6 xl:grid-cols-3 xl:gap-8"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <AnimatePresence mode="popLayout">
              {filteredProjects.map((project, index) => {
                const results: CaseResult[] = Array.isArray(project.results) ? project.results : []
                const [primaryResult] = results

                return (
                  <m.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.4, delay: index * 0.08 }}
                    className="min-w-[90vw] snap-center sm:min-w-[45vw] md:min-w-0"
                  >
                    <Link
                      href={`/cases/${project.id}`}
                      data-testid={`home-case-card-${project.id}`}
                      className="group relative block h-full"
                      onMouseEnter={() => prefetchRoute(`/cases/${project.id}`)}
                      onFocus={() => prefetchRoute(`/cases/${project.id}`)}
                    >
                      {/* 'group' moved here to individual card link */}

                      {/* Card Container */}
                      <div className="relative z-10 flex h-full flex-col overflow-hidden rounded-3xl border border-white/5 bg-[#0c1220] transition-all duration-300 contain-content hover:border-white/15">
                        {/* Image Section */}
                        <div className="relative h-[220px] shrink-0 overflow-hidden sm:h-[240px]">
                          <Image
                            src={project.image}
                            alt={project.title}
                            fill
                            sizes="(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 90vw"
                            className="object-cover transition-transform duration-500 group-hover:scale-105"
                          />

                          {/* Hover overlay */}
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                            <div className="flex h-12 w-12 scale-0 transform items-center justify-center rounded-full bg-white transition-transform duration-300 group-hover:scale-100">
                              <ArrowUpRight className="h-5 w-5 text-black" />
                            </div>
                          </div>
                        </div>

                        {/* Text Section */}
                        <div className="flex flex-grow flex-col p-6 sm:p-6">
                          {/* Title */}
                          <h3 className="home-card-title mb-3 line-clamp-2 text-white transition-colors group-hover:text-blue-400">
                            {project.title}
                          </h3>

                          <p className="home-subnote mb-6 line-clamp-1 text-gray-400">
                            {project.client}
                          </p>

                          {/* Results - Single premium KPI block */}
                          <div className="mt-auto border-t border-white/10 pt-5">
                            {primaryResult ? (
                              <div className="relative min-h-[88px] overflow-hidden rounded-2xl border border-white/10 bg-[linear-gradient(165deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0.02)_100%)] px-4 py-3">
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_0%_0%,rgba(255,255,255,0.12),transparent_45%)]" />
                                <p className="home-meta relative z-10 mb-1 tracking-[0.12em] text-gray-400 uppercase">
                                  Ключевой результат
                                </p>
                                <div className="relative z-10 flex min-w-0 items-center justify-between gap-3">
                                  <p className="text-[clamp(1.2rem,1.55vw,1.6rem)] leading-none font-bold whitespace-nowrap text-white">
                                    {primaryResult.value}
                                  </p>
                                  <p className="max-w-[12rem] truncate text-right text-xs leading-snug whitespace-nowrap text-gray-300 sm:text-sm">
                                    {primaryResult.label}
                                  </p>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </Link>
                  </m.div>
                )
              })}
            </AnimatePresence>
          </m.div>

          {/* Mobile Navigation Buttons */}
          <div className="mt-2 flex justify-center gap-4 md:hidden">
            <button
              onClick={() => {
                if (carouselRef.current) {
                  carouselRef.current.scrollBy({
                    left: -window.innerWidth * 0.85,
                    behavior: 'smooth',
                  })
                }
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-all hover:bg-white/10 active:scale-95"
              aria-label="Предыдущие кейсы"
            >
              <ArrowUpRight className="h-5 w-5 rotate-[-135deg]" />
            </button>
            <button
              onClick={() => {
                if (carouselRef.current) {
                  carouselRef.current.scrollBy({
                    left: window.innerWidth * 0.85,
                    behavior: 'smooth',
                  })
                }
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg transition-all active:scale-95"
              aria-label="Следующие кейсы"
            >
              <ArrowUpRight className="h-5 w-5 rotate-45" />
            </button>
          </div>
        </div>

        {/* View All Button */}
        <m.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-12 flex justify-center"
        >
          <Link
            href="/cases"
            onMouseEnter={() => prefetchRoute('/cases')}
            onFocus={() => prefetchRoute('/cases')}
            className="group flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3 text-base transition-all duration-300 hover:border-white/20 hover:bg-white/10 sm:gap-3 sm:px-8 sm:py-4 sm:text-lg"
          >
            Все проекты
            <ArrowUpRight className="h-5 w-5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
          </Link>
        </m.div>
      </div>
    </section>
  )
}
